import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { PLATES } from "../data/documents";

function PlateViewer({ index, onClose, onNav }) {
  const [scale, setScale] = useState(1);
  const plate = PLATES[index];
  const total = PLATES.length;

  const go = useCallback(
    (dir) => {
      setScale(1);
      onNav((index + dir + total) % total);
    },
    [index, total, onNav]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const clampScale = (v) => Math.min(4, Math.max(1, v));

  return (
    <motion.div
      data-testid="plate-viewer"
      className="fixed inset-0 z-[76] flex flex-col"
      style={{ background: "rgba(10, 9, 7, 0.985)" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.6 } }}
      exit={{ opacity: 0, transition: { duration: 0.4 } }}
    >
      <div className="flex items-center justify-between px-5 sm:px-8 py-4 z-10 gap-4">
        <button
          data-testid="plate-close"
          onClick={onClose}
          className="flex items-center gap-2 font-nav text-[10px] tracking-[0.3em] uppercase text-[#B8A795] hover:text-[#C5A059] transition-colors duration-500"
        >
          <X size={14} strokeWidth={1.5} />
          Close Document
        </button>
        <div className="flex items-center gap-3">
          <button
            data-testid="plate-zoom-out"
            onClick={() => setScale((s) => clampScale(s - 0.8))}
            aria-label="Zoom out"
            className="text-[#857565] hover:text-[#C5A059] transition-colors duration-500"
          >
            <ZoomOut size={16} strokeWidth={1.5} />
          </button>
          <span className="font-nav text-[9px] tracking-[0.25em] text-[#6f6252] w-10 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            data-testid="plate-zoom-in"
            onClick={() => setScale((s) => clampScale(s + 0.8))}
            aria-label="Zoom in"
            className="text-[#857565] hover:text-[#C5A059] transition-colors duration-500"
          >
            <ZoomIn size={16} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 flex items-center justify-center overflow-hidden px-3"
        onWheel={(e) => setScale((s) => clampScale(s + (e.deltaY < 0 ? 0.4 : -0.4)))}
        onDoubleClick={() => setScale((s) => (s > 1 ? 1 : 2.6))}
      >
        <motion.div
          animate={{ scale }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          drag={scale > 1}
          dragConstraints={{
            left: -900 * scale,
            right: 900 * scale,
            top: -650 * scale,
            bottom: 650 * scale,
          }}
          dragElastic={0.04}
          className={scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in"}
        >
          <AnimatePresence mode="wait">
            <motion.img
              key={plate.id}
              src={plate.image}
              alt={plate.name}
              data-testid="plate-image"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
              className="max-h-[72dvh] max-w-[94vw] w-auto object-contain select-none"
              draggable={false}
            />
          </AnimatePresence>
        </motion.div>
      </div>

      <div className="px-5 sm:px-8 pb-6 pt-3 z-10">
        <div className="flex items-end justify-between gap-6 max-w-4xl mx-auto w-full">
          <button
            data-testid="plate-prev"
            onClick={() => go(-1)}
            aria-label="Previous document"
            className="flex items-center gap-2 text-[#857565] hover:text-[#C5A059] transition-colors duration-500 pb-1"
          >
            <ChevronLeft size={16} strokeWidth={1.25} />
            <span className="hidden sm:inline font-nav text-[10px] tracking-[0.3em] uppercase">Prev</span>
          </button>
          <div className="text-center min-w-0">
            <p className="font-nav text-[9px] tracking-[0.45em] uppercase text-[#857565]">
              Reference Document · {index + 1} of {total}
            </p>
            <h3 className="font-display uppercase tracking-[0.12em] text-[#E8DCC4] text-lg sm:text-2xl mt-2">
              {plate.name}
            </h3>
            <p className="font-serif-body italic text-[#B8A795] text-xs sm:text-sm mt-1">
              {plate.note} — scroll or double-tap to enlarge
            </p>
          </div>
          <button
            data-testid="plate-next"
            onClick={() => go(1)}
            aria-label="Next document"
            className="flex items-center gap-2 text-[#857565] hover:text-[#C5A059] transition-colors duration-500 pb-1"
          >
            <span className="hidden sm:inline font-nav text-[10px] tracking-[0.3em] uppercase">Next</span>
            <ChevronRight size={16} strokeWidth={1.25} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function MasterPlan({ onClose }) {
  const [open, setOpen] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ wrapper: scrollRef.current, content: scrollRef.current.firstElementChild, lerp: 0.09 });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  return (
    <motion.div
      data-testid="master-plan-panel"
      className="fixed inset-0 z-[70] archival-vignette grain flex flex-col"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="px-6 sm:px-12 pt-8 pb-2 flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-nav text-[9px] tracking-[0.45em] uppercase text-[#857565]">
            Territorial &amp; Planning Documents
          </p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[0.12em] uppercase text-[#E8DCC4] mt-2">
            Master Plan
          </h2>
          <p className="font-serif-body italic text-[#9A8B78] text-sm sm:text-base mt-2">
            Reference plates — open any document, then scroll or double-tap to read it closely.
          </p>
        </div>
        <button
          data-testid="master-plan-close"
          onClick={onClose}
          aria-label="Close master plan"
          className="text-[#857565] hover:text-[#C5A059] transition-colors duration-500"
        >
          <X size={20} strokeWidth={1.25} />
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto archival-scroll px-6 sm:px-12 pb-28 pt-6">
        <div className="max-w-5xl mx-auto space-y-14">
          {PLATES.map((p, i) => (
            <motion.button
              key={p.id}
              data-testid={`plate-card-${p.id}`}
              onClick={() => setOpen(i)}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * i, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="index-card group text-left w-full"
            >
              <div className="manuscript-frame overflow-hidden" style={{ aspectRatio: "3/2" }}>
                <img src={p.image} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="flex items-baseline gap-3 mt-4">
                <span className="font-nav text-[9px] tracking-[0.2em] text-[#6f6252]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0">
                  <div className="font-display text-lg sm:text-xl tracking-[0.1em] uppercase text-[#D6C5B3] group-hover:text-[#C5A059] transition-colors duration-500">
                    {p.name}
                  </div>
                  <div className="font-nav text-[9px] tracking-[0.25em] uppercase text-[#857565] mt-1">
                    {p.note}
                  </div>
                  <div className="card-line h-px bg-[#C5A059] mt-2 w-16" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {open !== null && (
          <PlateViewer key="plate" index={open} onClose={() => setOpen(null)} onNav={setOpen} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
